import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { SlidersHorizontal } from "lucide-react";

/** The real canvas colour/stroke controls, shared by inline and Focus toolbars. */
export function DrawingStyle({
  children,
  onOpen,
}: {
  children: ReactNode;
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape, true);
    };
  }, [open]);
  return (
    <div ref={root} className="drawing-style">
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() =>
          setOpen((value) => {
            const next = !value;
            if (next) onOpen?.();
            return next;
          })
        }
      >
        <SlidersHorizontal size={18} />
        <span className="sr-only">Drawing style</span>
      </button>
      <div
        id={id}
        hidden={!open}
        className="drawing-style-popover"
        role="group"
        aria-label="Drawing style"
      >
        {children}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            trigger.current?.focus();
          }}
          className="drawing-style-done"
        >
          Done
        </button>
      </div>
    </div>
  );
}
